import { useState, useEffect, useId } from "react";
import { createProspect } from "../../../../services/prospects";
import { TextInput } from "../../../../components/TextInput";
import { Button } from "../../../../components/Button";
import { TeamTypeahead } from "../../../../components/TeamTypeahead";
import type { TeamOption } from "../../../../components/TeamTypeahead";
import { SelectPopover } from "../../../../components/SelectPopover";
import type { ProspectResponse } from "../../../../types/api";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import {
  PLAYER_LEVEL_LABEL_OPTIONS,
  isJucoPlayerLevel,
  playerLevelFromLabel,
} from "../../../../constants/playerLevels";
import {
  JUCO_YEAR_LABEL_OPTIONS,
  jucoYearFromLabel,
} from "../../../../constants/jucoYears";
import "./AddProspectPanel.css";

interface AddProspectPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (prospect: ProspectResponse) => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  jerseyNumber: string;
  team: TeamOption | null;
  gradYear: string;
  position: string;
  /** Level and JC year are held as the labels the selects show; see constants/jucoYears.ts. */
  playerLevel: string;
  jucoYear: string;
}

const EMPTY_FORM: FormData = {
  firstName: "",
  lastName: "",
  jerseyNumber: "",
  team: null,
  gradYear: "",
  position: "",
  playerLevel: "",
  jucoYear: "",
};

interface FormErrors {
  firstName?: string;
  lastName?: string;
  jerseyNumber?: string;
  team?: string;
}

export const AddProspectPanel = ({
  isOpen,
  onClose,
  onSuccess,
}: AddProspectPanelProps) => {
  // The six fields above these two carry hardcoded ids that predate the `useId` convention in
  // web/CLAUDE.md; the new fields follow it, so two mounted panels cannot collide on the id that
  // `aria-describedby` points at.
  const uid = useId();
  const levelFieldId = `${uid}-level`;
  const jcYearFieldId = `${uid}-jc-year`;
  const jcYearHintId = `${uid}-jc-year-hint`;
  const teamHintId = `${uid}-team-hint`;

  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset form when panel closes
  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setErrors({});
      setSaveError(null);
    }
  }, [isOpen]);

  // ESC key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isSaving) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isSaving, onClose]);

  // Read back through the shared table rather than trusting the label string, so `0` (High
  // School) arrives as the number 0 and not as "absent".
  const playerLevel = playerLevelFromLabel(formData.playerLevel);
  const isJucoSelected = isJucoPlayerLevel(playerLevel);

  // A JC year belongs to the JUCO ladder only. Dropping it when the level moves off JUCO stops a
  // stale "JC 2" riding along on a High School player who was retyped mid-form.
  const handleLevelChange = (label: string) => {
    setFormData((prev) => ({
      ...prev,
      playerLevel: label,
      jucoYear: isJucoPlayerLevel(playerLevelFromLabel(label)) ? prev.jucoYear : "",
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim() && !formData.lastName.trim()) {
      newErrors.firstName = "Enter at least a first or last name";
    }

    if (!formData.jerseyNumber.trim()) {
      newErrors.jerseyNumber = "Jersey number is required";
    }

    if (!formData.team) {
      newErrors.team = "Pick a team, or type a new team name and create it";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      // The createProspect API accepts team IDs (GUIDs) or team names (strings)
      // If team is new, pass the name directly and the API will create it
      // If team exists, pass the ID
      const teamValue =
        formData.team!.isNew || !formData.team!.id
          ? formData.team!.name
          : formData.team!.id;

      // Create prospect
      const createdProspect = await createProspect({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        jerseyNumber: parseInt(formData.jerseyNumber.trim(), 10),
        teams: [teamValue],
        gradYear: formData.gradYear
          ? parseInt(formData.gradYear, 10)
          : undefined,
        position: formData.position.trim() || undefined,
        // `?? undefined`, never `|| undefined`: High School is the ordinal 0 and would be
        // dropped from the payload by a truthiness test.
        playerLevel: playerLevel ?? undefined,
        jucoYear: (isJucoSelected ? jucoYearFromLabel(formData.jucoYear) : null) ?? undefined,
      });

      onClose();
      onSuccess(createdProspect);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to save prospect"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  };

  return (
    <>
      <div
        className={`add-prospect-panel-overlay ${
          isOpen ? "add-prospect-panel-overlay--open" : ""
        }`}
        onClick={handleOverlayClick}
      />
      <div
        className={`add-prospect-panel ${
          isOpen ? "add-prospect-panel--open" : ""
        }`}
      >
        <div className="add-prospect-panel__content">
          {/* Header */}
          <div className="add-prospect-panel__header">
            <h2 className="add-prospect-panel__title">Add Player</h2>
            <ModalCloseButton onClick={onClose} disabled={isSaving} />
          </div>

          {/* Form */}
          <form
            className="add-prospect-panel__form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            {/* First Name */}
            <div>
              <label htmlFor="add-prospect-panel-first-name-0" className="add-prospect-panel__label">First Name</label>
              <TextInput id="add-prospect-panel-first-name-0"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                placeholder="First Name"
                error={errors.firstName}
                disabled={isSaving}
                style={{ textTransform: "capitalize" }}
              />
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="add-prospect-panel-last-name-1" className="add-prospect-panel__label">Last Name</label>
              <TextInput id="add-prospect-panel-last-name-1"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                placeholder="Last Name"
                error={errors.lastName}
                disabled={isSaving}
                style={{ textTransform: "capitalize" }}
              />
            </div>

            {/* Jersey Number */}
            <div>
              <label htmlFor="add-prospect-panel-jersey-number-2" className="add-prospect-panel__label">Jersey Number</label>
              <TextInput id="add-prospect-panel-jersey-number-2"
                type="number"
                value={formData.jerseyNumber}
                onChange={(e) =>
                  setFormData({ ...formData, jerseyNumber: e.target.value })
                }
                placeholder="Number"
                error={errors.jerseyNumber}
                disabled={isSaving}
              />
            </div>

            {/* Team/School */}
            <div>
              <label htmlFor="add-prospect-panel-team-school-0" className="add-prospect-panel__label">Team/School (Required)</label>
              <TeamTypeahead inputId="add-prospect-panel-team-school-0"
                value={formData.team}
                onSelect={(team) => setFormData({ ...formData, team })}
                placeholder="Type a team name"
                error={errors.team}
                describedBy={teamHintId}
                required
              />
              <p id={teamHintId} className="add-prospect-panel__hint">
                Every player needs to be attached to a team so you can find them later.
              </p>
            </div>

            {/* Grad Year (Optional) */}
            <div>
              <label htmlFor="add-prospect-panel-grad-year-optional-3" className="add-prospect-panel__label">
                Grad Year (Optional)
              </label>
              <TextInput id="add-prospect-panel-grad-year-optional-3"
                type="number"
                value={formData.gradYear}
                onChange={(e) =>
                  setFormData({ ...formData, gradYear: e.target.value })
                }
                placeholder="2025"
                disabled={isSaving}
              />
            </div>

            {/* Position (Optional) */}
            <div>
              <label htmlFor="add-prospect-panel-position-optional-4" className="add-prospect-panel__label">
                Position (Optional)
              </label>
              <TextInput id="add-prospect-panel-position-optional-4"
                value={formData.position}
                onChange={(e) =>
                  setFormData({ ...formData, position: e.target.value })
                }
                placeholder="Position"
                disabled={isSaving}
                style={{ textTransform: "uppercase" }}
              />
            </div>

            {/* Level (Optional) */}
            <div>
              <label htmlFor={levelFieldId} className="add-prospect-panel__label">
                Level (Optional)
              </label>
              <SelectPopover
                id={levelFieldId}
                testId="add-prospect-level"
                options={PLAYER_LEVEL_LABEL_OPTIONS}
                value={formData.playerLevel}
                onChange={handleLevelChange}
                placeholder="Select level"
                disabled={isSaving}
                fullWidth
              />
            </div>

            {/* JC Year (Optional) - a JUCO-only field, so it stays inert until Level says JUCO */}
            <div>
              <label htmlFor={jcYearFieldId} className="add-prospect-panel__label">
                JC Year (Optional)
              </label>
              <SelectPopover
                id={jcYearFieldId}
                testId="add-prospect-jc-year"
                describedById={isJucoSelected ? undefined : jcYearHintId}
                options={JUCO_YEAR_LABEL_OPTIONS}
                value={formData.jucoYear}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, jucoYear: value }))
                }
                placeholder="Select JC year"
                disabled={isSaving || !isJucoSelected}
                fullWidth
              />
              {!isJucoSelected && (
                <p id={jcYearHintId} className="add-prospect-panel__hint">
                  Set Level to JUCO to choose a JC year.
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="add-prospect-panel__footer">
              <Button
                variant="primary"
                type="submit"
                disabled={isSaving}
                fullWidth
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </div>

            {/* Error */}
            {saveError && (
              <div className="add-prospect-panel__error">{saveError}</div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};
